import { useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import {
  Activity,
  AlertTriangle,
  CheckCheck,
  FolderKanban,
  Plus,
  RefreshCw,
} from "lucide-react";
import {
  addTaskParticipant,
  assignTask,
  changeTaskState,
  createSubtask,
  createTask,
  createTaskComment,
  deleteSubtask,
  deleteTask,
  deleteTaskAttachment,
  downloadTaskAttachment,
  getTask,
  removeTaskParticipant,
  reorderTasks,
  startTaskTime,
  stopTaskTime,
  toggleSubtask,
  uploadTaskAttachments,
} from "../api/tasks";
import { WorkspaceHeader } from "@shared/components/WorkspaceHeader";
import { useGlobalNavigationTarget } from "@shared/navigation/globalNavigation";
import { TaskCreateModal } from "../components/TaskCreateModal";
import { TaskDashboardPanel } from "../components/TaskDashboardPanel";
import { TaskDetailDrawer } from "../components/TaskDetailDrawer";
import { TaskKanbanBoard } from "../components/TaskKanbanBoard";
import { TaskSectionNav } from "../components/TaskSectionNav";
import { TaskTableBoard } from "../components/TaskTableBoard";
import { TaskToolbar, type TaskFilters } from "../components/TaskToolbar";
import { useTasks } from "../hooks/useTasks";
import {
  buildTaskGroups,
  isTaskOverdue,
  type Task,
  type TaskView,
} from "../types/tasks";
import "../styles/tasks.css";

const defaultFilters: TaskFilters = {
  search: "",
  estado: "",
  prioridad: "",
  proyecto: "",
  responsable: "",
  onlyMine: false,
  overdueOnly: false,
};

export function TasksPage() {
  const { tasks, meta, loading, reload, replaceTask, setTasks } = useTasks();
  const [activeView, setActiveView] = useState<TaskView>("kanban");
  const [filters, setFilters] = useState<TaskFilters>(defaultFilters);
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useGlobalNavigationTarget("tasks", (target) => {
    switch (target.section) {
      case "kanban":
      case "tabla":
      case "dashboard":
        setActiveView(target.section);
        break;
      default:
        break;
    }
  });

  const filteredTasks = useMemo(() => {
    const query = filters.search.trim().toLowerCase();

    return tasks.filter((task) => {
      const searchText = [
        task.titulo,
        task.descripcion,
        task.proyecto_nombre,
        task.asignado_nombre,
        task.creado_por_nombre,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesQuery = query === "" || searchText.includes(query);
      const matchesState = filters.estado === "" || task.estado === filters.estado;
      const matchesPriority =
        filters.prioridad === "" || task.prioridad === filters.prioridad;
      const matchesProject =
        filters.proyecto === ""
          ? true
          : filters.proyecto === "none"
            ? task.proyecto_id === null
            : String(task.proyecto_id ?? "") === filters.proyecto;
      const matchesResponsible =
        filters.responsable === ""
          ? true
          : filters.responsable === "unassigned"
            ? task.asignado_id === null
            : String(task.asignado_id ?? "") === filters.responsable;
      const matchesMine =
        !filters.onlyMine ||
        task.asignado_id === meta.current_user.id ||
        task.creado_por === meta.current_user.id;
      const matchesOverdue = !filters.overdueOnly || isTaskOverdue(task);

      return (
        matchesQuery &&
        matchesState &&
        matchesPriority &&
        matchesProject &&
        matchesResponsible &&
        matchesMine &&
        matchesOverdue
      );
    });
  }, [filters, meta.current_user.id, tasks]);

  const selectedTask = useMemo(
    () => tasks.find((task) => task.id === selectedTaskId) ?? null,
    [selectedTaskId, tasks],
  );

  const groups = useMemo(() => buildTaskGroups(filteredTasks), [filteredTasks]);
  const overdueCount = useMemo(
    () => filteredTasks.filter(isTaskOverdue).length,
    [filteredTasks],
  );

  const heroMetrics = useMemo(
    () => ({
      total: filteredTasks.length,
      active: filteredTasks.filter((task) => task.estado !== "completado").length,
      overdue: overdueCount,
      done: filteredTasks.filter((task) => task.estado === "completado").length,
    }),
    [filteredTasks, overdueCount],
  );

  useEffect(() => {
    if (!selectedTaskId) return;

    const timer = window.setInterval(() => {
      void getTask(selectedTaskId)
        .then((task) => replaceTask(task))
        .catch(() => {});
    }, 20000);

    return () => window.clearInterval(timer);
  }, [replaceTask, selectedTaskId]);

  const openTask = (task: Task) => {
    setSelectedTaskId(task.id);
    setDrawerOpen(true);
  };

  const withBusy = async (runner: () => Promise<void>) => {
    setBusy(true);

    try {
      await runner();
    } finally {
      setBusy(false);
    }
  };

  const applyTaskOrder = (source: Task[], ids: number[]) =>
    source.map((task) => {
      const index = ids.indexOf(task.id);
      return index === -1 ? task : { ...task, orden: index + 1 };
    });

  const reorderGroup = async (ids: number[]) => {
    const previousTasks = tasks;
    setTasks((current) => applyTaskOrder(current, ids));

    try {
      await reorderTasks(ids);
    } catch (error) {
      setTasks(previousTasks);
      throw error;
    }
  };

  const removeTaskFromState = (taskId: number) => {
    setTasks((current) => current.filter((task) => task.id !== taskId));
    if (selectedTaskId === taskId) {
      setSelectedTaskId(null);
      setDrawerOpen(false);
    }
  };

  const openCreateModal = () => {
    setCreateError(null);
    setCreateOpen(true);
  };

  const refreshData = async () => {
    setRefreshing(true);

    try {
      await reload();
      if (selectedTaskId) {
        replaceTask(await getTask(selectedTaskId));
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="page-shell tasks-page-shell !gap-4">
      <WorkspaceHeader
        eyebrow="Task Operations"
        title="Tasks Workspace"
        description="Proyectos, responsables, subtareas, actividad, tiempo y archivos en una experiencia de trabajo clara."
        icon={FolderKanban}
        loading={loading}
        metrics={[
          { label: "Total filtrado", value: heroMetrics.total, icon: FolderKanban },
          { label: "Activas", value: heroMetrics.active, icon: Activity },
          { label: "Vencidas", value: heroMetrics.overdue, icon: AlertTriangle, tone: "amber" },
          { label: "Completadas", value: heroMetrics.done, icon: CheckCheck, tone: "emerald" },
        ]}
        actions={
          <>
            <button
              type="button"
              onClick={() => void refreshData()}
              disabled={refreshing || loading}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#00d09c]/22 bg-white/90 px-4 py-2.5 text-sm font-black text-[#0b4a2f] shadow-[0_12px_28px_rgba(11,74,47,.09)] transition hover:-translate-y-0.5 hover:bg-[#00d09c]/8 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw size={15} className={refreshing || loading ? "animate-spin" : ""} />
              Actualizar datos
            </button>

            {meta.permissions.can_create && (
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 rounded-full bg-[linear-gradient(135deg,#0b4a2f_0%,#0f5b3b_48%,#00a879_100%)] px-4 py-2.5 text-sm font-black text-white shadow-[0_16px_34px_rgba(11,74,47,.18)] xl:hidden"
              >
                <Plus size={15} />
                Nueva tarea
              </button>
            )}
          </>
        }
      />

      <div className="tasks-workspace-grid min-w-0 max-w-full overflow-x-hidden">
        <section className="flex min-w-0 max-w-full flex-col gap-3 overflow-x-hidden">
          <TaskSectionNav active={activeView} tasks={filteredTasks} onChange={setActiveView} />

          <TaskToolbar
            meta={meta}
            filters={filters}
            totalTasks={filteredTasks.length}
            overdueCount={overdueCount}
            onFiltersChange={(patch) =>
              setFilters((current) => ({ ...current, ...patch }))
            }
            onOpenCreate={openCreateModal}
          />

          <div className="min-w-0 overflow-x-hidden">
            {activeView === "dashboard" && <TaskDashboardPanel tasks={filteredTasks} />}

            {activeView === "kanban" && (
              <TaskTableBoard
                tasks={filteredTasks}
                currentUserId={meta.current_user.id}
                onOpenTask={openTask}
                onDeleteTask={(task) => {
                  if (!window.confirm("¿Eliminar esta tarea?")) return;
                  void withBusy(async () => {
                    await deleteTask(task.id);
                    removeTaskFromState(task.id);
                  });
                }}
              />
            )}

            {activeView === "tabla" && (
              <TaskKanbanBoard
                groups={groups}
                canReorder={meta.permissions.can_reorder}
                onOpenTask={openTask}
                onReorder={reorderGroup}
              />
            )}
          </div>
        </section>
      </div>

      <TaskCreateModal
        open={createOpen}
        meta={meta}
        submitting={createBusy}
        error={createError}
        onClose={() => {
          setCreateError(null);
          setCreateOpen(false);
        }}
        onSubmit={async (payload) => {
          setCreateBusy(true);
          setCreateError(null);

          try {
            const task = await createTask(payload);
            replaceTask(task);
            setSelectedTaskId(task.id);
            setCreateOpen(false);
            setDrawerOpen(true);
            setActiveView("kanban");
          } catch (error) {
            if (isAxiosError(error)) {
              const errorBag = error.response?.data?.errors;
              const firstFieldError =
                errorBag && typeof errorBag === "object"
                  ? Object.values(errorBag).find(Array.isArray)?.[0]
                  : null;
              const fallbackMessage =
                typeof error.response?.data?.message === "string"
                  ? error.response.data.message
                  : "No se pudo crear la tarea.";

              setCreateError(
                typeof firstFieldError === "string"
                  ? firstFieldError
                  : fallbackMessage,
              );
              return;
            }

            setCreateError("No se pudo crear la tarea.");
          } finally {
            setCreateBusy(false);
          }
        }}
      />

      <TaskDetailDrawer
        open={drawerOpen}
        task={selectedTask}
        meta={meta}
        busy={busy}
        onClose={() => setDrawerOpen(false)}
        onChangeState={(task, state) =>
          withBusy(async () => {
            replaceTask(await changeTaskState(task.id, state));
          })
        }
        onAssign={(task, userId) =>
          withBusy(async () => {
            replaceTask(await assignTask(task.id, userId));
          })
        }
        onAddParticipant={(task, userId, permission) =>
          withBusy(async () => {
            replaceTask(await addTaskParticipant(task.id, userId, permission));
          })
        }
        onRemoveParticipant={(task, userId) =>
          withBusy(async () => {
            replaceTask(await removeTaskParticipant(task.id, userId));
          })
        }
        onCreateSubtask={(task, title) =>
          withBusy(async () => {
            replaceTask(await createSubtask(task.id, title));
          })
        }
        onToggleSubtask={(task, subtaskId) =>
          withBusy(async () => {
            replaceTask(await toggleSubtask(task.id, subtaskId));
          })
        }
        onDeleteSubtask={(task, subtaskId) =>
          withBusy(async () => {
            replaceTask(await deleteSubtask(task.id, subtaskId));
          })
        }
        onSendComment={(task, value) =>
          withBusy(async () => {
            await createTaskComment(task.id, value);
            replaceTask(await getTask(task.id));
          })
        }
        onUploadAttachments={(task, files) =>
          withBusy(async () => {
            replaceTask(await uploadTaskAttachments(task.id, files));
          })
        }
        onDownloadAttachment={async (attachment) => {
          await downloadTaskAttachment(attachment);
        }}
        onDeleteAttachment={(attachment) =>
          withBusy(async () => {
            replaceTask(await deleteTaskAttachment(attachment.id));
          })
        }
        onStartTime={(task) =>
          withBusy(async () => {
            replaceTask(await startTaskTime(task.id));
          })
        }
        onStopTime={(task, entry) =>
          withBusy(async () => {
            replaceTask(await stopTaskTime(task.id, entry.id));
          })
        }
        onDeleteTask={(task) =>
          withBusy(async () => {
            await deleteTask(task.id);
            removeTaskFromState(task.id);
          })
        }
      />
    </div>
  );
}
